import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LOGO_URL } from "../lib/accents";

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/invites/${token}`)
      .then((r) => {
        setInvite(r.data);
        setFirstName(r.data.first_name || (r.data.name ? r.data.name.split(" ")[0] : ""));
        setLastName(r.data.last_name || (r.data.name ? r.data.name.split(" ").slice(1).join(" ") : ""));
      })
      .catch((e) => setError(apiError(e)));
  }, [token]);

  const hasNames = !!(invite?.first_name || invite?.last_name || invite?.name);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    const fn = firstName.trim();
    const ln = lastName.trim();
    if (!hasNames && (!fn || !ln)) {
      toast.error("First name and last name are required");
      return;
    }
    setLoading(true);
    try {
      const payload = { password };
      if (!hasNames || fn) payload.first_name = fn;
      if (!hasNames || ln) payload.last_name = ln;
      const { data } = await api.post(`/invites/${token}/accept`, payload);
      setUser(data);
      toast.success("Welcome to the Lookbook!");
      navigate("/");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const fullName = [firstName, lastName].filter(Boolean).join(" ");

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-8">
      <div className="w-full max-w-md fade-up">
        <img src={LOGO_URL} alt="UnCloned®" className="h-7 w-auto" />
        {error ? (
          <div className="mt-10" data-testid="invite-error">
            <h2 className="font-display font-black text-3xl text-[#000000]">Invite unavailable</h2>
            <p className="text-gray-500 mt-3">{error}</p>
            <p className="text-sm text-gray-400 mt-4">Ask your UnCloned account manager to send a fresh invite.</p>
          </div>
        ) : !invite ? (
          <div className="mt-10 text-gray-400">Checking your invite...</div>
        ) : (
          <>
            <h2 className="font-display font-black text-3xl text-[#000000] mt-10" data-testid="invite-hello">
              {hasNames && firstName ? `Hi ${firstName},` : "You're invited"}
            </h2>
            <p className="text-gray-500 mt-2 text-sm">
              {hasNames ? "Set a password below to activate your UnCloned® Ads Lookbook account" : "Tell us your name and set a password to activate your UnCloned® Ads Lookbook account"}
              {hasNames && fullName ? <> for <span className="font-bold text-[#000000]">{fullName}</span></> : null}
              {" "}(<span className="font-mono">{invite.email}</span>).
            </p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              {!hasNames && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">First name</label>
                    <input data-testid="invite-first-name-input" required value={firstName} onChange={(e) => setFirstName(e.target.value)}
                      className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
                      placeholder="Jane" />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Last name</label>
                    <input data-testid="invite-last-name-input" required value={lastName} onChange={(e) => setLastName(e.target.value)}
                      className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
                      placeholder="Doe" />
                  </div>
                </div>
              )}
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Password</label>
                <input data-testid="invite-password-input" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                  className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
                  placeholder="At least 8 characters" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Confirm password</label>
                <input data-testid="invite-confirm-input" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
                  placeholder="Repeat password" />
              </div>
              <button data-testid="invite-submit-btn" type="submit" disabled={loading}
                className="w-full bg-[#D0FF0D] text-[#000000] rounded-full px-6 py-3 font-bold text-sm hover:bg-[#0FFFE2] transition-colors duration-200 disabled:opacity-60">
                {loading ? "Activating..." : "Activate account & log in"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
